import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { formatPct, formatSignedPct, formatUsd, timeAgo, type VaultSummary } from "@v4ult/shared";
import { ArrowRight, Plus } from "lucide-react";
import { motion } from "motion/react";
import { useEffect } from "react";
import { AllocBar, mixColorMap, Page, PageHead, Pill, ServiceNote } from "../components/app-ui";
import { AssetIcon } from "../components/asset";
import { ChainGlyph, Change, chainName, DriftMeter, StatusDot } from "../components/vault-bits";
import { useAuthState } from "../components/wallet";
import { useAppConfig, vaultsQuery } from "../lib/api";

export const Route = createFileRoute("/app/")({ component: Dashboard });

/** A wallet with no vaults is sent on to the builder once per visit, so closing the builder still lands here. */
let sentToBuilder = false;

function Dashboard() {
  const { signedIn } = useAuthState();
  const navigate = useNavigate();
  // Signed out, the builder is open to everyone; only the vault list waits for a wallet.
  const { data, isPending: loading, error } = useQuery({ ...vaultsQuery, enabled: signedIn });
  const vaults = signedIn ? data : undefined;
  const isPending = signedIn && loading;
  useEffect(() => {
    if (vaults?.length === 0 && !sentToBuilder) {
      sentToBuilder = true;
      void navigate({ to: "/app/new", replace: true });
    }
  }, [vaults, navigate]);
  const n = vaults?.length ?? 0;
  const total = vaults?.reduce((s, v) => s + v.totalUsd, 0) ?? 0;
  const priced = vaults?.filter((v) => v.change24hPct !== null) ?? [];
  const pricedUsd = priced.reduce((s, v) => s + v.totalUsd, 0);
  const change = pricedUsd
    ? priced.reduce((s, v) => s + v.totalUsd * (v.change24hPct ?? 0), 0) / pricedUsd
    : null;
  const active = vaults?.filter((v) => v.status === "active").length ?? 0;

  return (
    <Page>
      <PageHead
        eyebrow={
          <>
            <Pill>{signedIn ? `Portfolio · ${n} vault${n === 1 ? "" : "s"}` : "Portfolio"}</Pill>
            <ServiceNote />
          </>
        }
        title="Your vaults"
        lead={
          n > 0 ? (
            <span className="flex items-center gap-2">
              <span
                className="ds-dot size-2.5 bg-[#18a957]"
                data-pulse={active ? "on" : "off"}
                aria-hidden="true"
              />
              {active} of {n} agent{n === 1 ? "" : "s"} on duty
            </span>
          ) : isPending ? null : signedIn ? (
            "Pick a mix and rules, then create your vault."
          ) : (
            "Pick a mix and rules, then connect your wallet to create your vault. Already have one? Connect to see it."
          )
        }
        actions={
          <Link to="/app/new" className="ap-btn ap-btn-pink ap-btn-md">
            <Plus size={18} aria-hidden="true" /> New vault
          </Link>
        }
        aside={
          n > 0 && (
            <div>
              <p className="ap-mono">Total value</p>
              <p className="mt-2 text-[clamp(40px,4.6vw,60px)] leading-[1] font-[550] tracking-[-0.045em] tabular-nums">
                {formatUsd(total)}
              </p>
              {change !== null && (
                <p className="mt-2 font-mono text-[14px] text-(--ap-grey) tabular-nums">
                  {change > 0 ? "▲" : change < 0 ? "▼" : ""} {formatSignedPct(change)} in 24h
                </p>
              )}
            </div>
          )
        }
      />

      {error && (
        <p role="alert" className="mt-8 text-bad">
          {error.message}
        </p>
      )}
      <div className="mt-10 grid grid-cols-[minmax(0,1fr)] gap-6 md:grid-cols-2 xl:grid-cols-3">
        {isPending &&
          ["s1", "s2", "s3"].map((k) => (
            <div key={k} className="ap-card h-[420px] animate-pulse motion-reduce:animate-none" />
          ))}
        {vaults?.map((v, i) => (
          <motion.div
            key={v.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="min-w-0"
          >
            <VaultCard vault={v} />
          </motion.div>
        ))}
        {(vaults || !signedIn) && <NewVaultCard first={n === 0} />}
      </div>
    </Page>
  );
}

function VaultCard({ vault }: { vault: VaultSummary }) {
  const { data: config } = useAppConfig();
  const chain = config?.chains.find((c) => c.id === vault.chainId);
  const color = mixColorMap(vault.targets.map((t) => t.symbol));
  const shown = vault.targets.slice(0, 5);
  return (
    <Link
      to="/app/vault/$id"
      params={{ id: vault.id }}
      className="ap-card group flex h-full flex-col p-5 transition-transform sm:p-6 duration-300 hover:-translate-y-1 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="ap-mono flex items-center gap-1.5">
            <ChainGlyph chainId={vault.chainId} size={12} /> {chainName(vault.chainId)}
          </p>
          <h2 className="mt-2 truncate text-[22px] font-[550] tracking-[-0.035em]">{vault.name}</h2>
        </div>
      </div>

      <div className="mt-4 flex items-baseline justify-between gap-3">
        <span className="text-[30px] leading-none font-[550] tracking-[-0.04em] tabular-nums">
          {formatUsd(vault.totalUsd)}
        </span>
        <Change pct={vault.change24hPct} className="text-[13.5px]" />
      </div>

      <AllocBar
        className="mt-6"
        items={vault.targets.map((t) => ({ symbol: t.symbol, bps: t.weightBps, color: color(t.symbol) }))}
      />
      {/* phones: one wrapped line of weights instead of a row per asset */}
      <p className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[13px] text-(--ap-grey) sm:hidden">
        {vault.targets.map((t) => (
          <span key={t.symbol} className="inline-flex items-center gap-1.5">
            <i className="size-1.5 rounded-full" style={{ background: color(t.symbol) }} aria-hidden="true" />
            <span className="font-[550] text-(--ap-ink)">{t.symbol}</span>
            <span className="font-mono tabular-nums">
              {formatPct(t.weightBps, t.weightBps % 100 ? 1 : 0)}
            </span>
          </span>
        ))}
      </p>
      <ul className="mt-4 grid gap-2.5 max-sm:hidden" aria-label="Target weights">
        {shown.map((t) => (
          <li key={t.symbol} className="flex items-center gap-2.5 text-[14.5px]">
            <AssetIcon asset={chain?.assets.find((a) => a.symbol === t.symbol)} symbol={t.symbol} size={24} />
            <span className="min-w-0 flex-1 truncate font-[550]">{t.symbol}</span>
            <i className="size-2 rounded-full" style={{ background: color(t.symbol) }} aria-hidden="true" />
            <span className="w-12 text-right font-mono text-[13px] tabular-nums">
              {formatPct(t.weightBps, t.weightBps % 100 ? 1 : 0)}
            </span>
          </li>
        ))}
        {vault.targets.length > shown.length && (
          <li className="text-[13.5px] text-(--ap-grey)">+{vault.targets.length - shown.length} more</li>
        )}
      </ul>

      <div className="mt-auto pt-5 sm:pt-6">
        <DriftMeter driftBps={vault.maxDriftBps} bandBps={vault.rules.driftThresholdBps} />
        <div className="mt-5 flex items-center justify-between gap-3 border-t border-(--ap-line) pt-4">
          <StatusDot status={vault.status} />
          <span className="text-[13px] text-(--ap-grey)">
            {vault.lastRebalancedAt
              ? `Rebalanced ${timeAgo(vault.lastRebalancedAt)}`
              : `Checked ${timeAgo(vault.lastCheckedAt)}`}
          </span>
        </div>
      </div>
    </Link>
  );
}

function NewVaultCard({ first }: { first: boolean }) {
  return (
    <Link
      to="/app/new"
      className="group flex min-h-[320px] flex-col justify-between rounded-[28px] border-[1.5px] border-dashed border-black/15 p-6 transition-colors hover:border-(--ap-pink) hover:bg-white"
    >
      <span
        aria-hidden="true"
        className="grid size-14 place-items-center rounded-[18px] bg-(--ap-pink) text-white shadow-[0_14px_30px_-14px_rgba(0,0,0,0.6)] transition-transform duration-500 group-hover:rotate-[8deg] motion-reduce:transition-none"
      >
        <Plus size={26} strokeWidth={2} />
      </span>
      <div>
        <h2 className="text-[22px] font-[550] tracking-[-0.035em]">
          {first ? "Open your first vault" : "Open another vault"}
        </h2>
        <p className="mt-2 text-[15px] leading-[1.45] text-(--ap-grey)">
          Pick assets and target weights, or start from a preset, then create your vault.
        </p>
        <span className="mt-5 inline-flex items-center gap-2 text-[15px] font-[550] text-(--ap-pink)">
          Build a vault <ArrowRight size={16} aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
}
